// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useRef, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { formatBaht } from '@/lib/money'
import {
  User,
  Home,
  Calendar,
  DollarSign,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Download,
  Link as LinkIcon,
  FileSignature,
  Paperclip,
  RefreshCw,
  Car,
  ScanLine,
  CreditCard,
  Wallet,
  HandCoins,
  Repeat,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Alert, Skeleton, ConfirmDialog, Modal, Input, Select, DateInput } from '@/components/ui'
import { FileUpload } from '@/components/ui/FileUpload'
import { useLease, useUpdateLease, useDeleteLease, useGenerateSignLinks, useLeaseSignatures, useRegenerateSignLink, useCreateDepositInvoice, useRecordDepositHeld, useEndLease, useRenewLease, leaseKeys } from '@/hooks/useLeases'
import { useVehiclesByLease, useUpdateVehicle, useCreateVehicle, vehicleKeys } from '@/hooks/useVehicles'
import { useLeases } from '@/hooks/useLeases'
import { useCustomers } from '@/hooks/useCustomers'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useDateFormatter, useDateTimeFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useAuth } from '@/hooks/useAuth'
// WEB-H7: PDF generator is dynamic-imported at the call site (pdfme weight).
import { api } from '@/lib/api'
import { resolveImageUrl, ImageNotFoundError } from '@/lib/imageUrl'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useForm } from 'react-hook-form'
import type { VehicleType } from '@/types/parking'
import type { DepositSummary } from '@/types'
import {
  VehiclePhotoUpload,
  uploadVehiclePhoto,
} from '@/components/vehicles/VehiclePhotoUpload'
import { LprVerificationDrawer } from '@/components/lpr/LprVerificationDrawer'
import { useLprEvents } from '@/hooks/useLprEvents'
import { ExploreCoachBanner } from '@/components/onboarding/ExploreCoachBanner'
import { SupportSessionHidden } from '@/components/SupportSessionHidden'
import { hiddenInSession, removedInSession } from '@/lib/sessionRedaction'

const API_BASE = import.meta.env.VITE_API_URL || ''

async function uploadLeaseFile(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

// Store raw S3 keys for lease images/attachments; display via /api/images/:key
interface AttachmentListProps {
  attachmentUrls: string[]
  label: string
  uploadLabel: string
  removeLabel: string
  apiBase: string
  onRemove: (updated: string[]) => Promise<void>
  onUpload: (file: File) => Promise<void>
}

function AttachmentList({ attachmentUrls, label, uploadLabel, removeLabel, apiBase, onRemove, onUpload }: AttachmentListProps) {
  const [isUploading, setIsUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploading(true)
    try {
      await onUpload(file)
    } finally {
      setIsUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div>
      <h3 className="text-sm font-medium text-base-content/70 mb-3">{label}</h3>
      {attachmentUrls.length > 0 && (
        <div className="space-y-2 mb-3">
          {attachmentUrls.map((key, idx) => {
            const url = key.startsWith('/api/') || key.startsWith('http')
              ? key
              : `${apiBase}/api/images/${key}`
            const filename = key.split('/').pop() || `file-${idx + 1}`
            return (
              <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-base-200">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline truncate max-w-xs"
                >
                  {filename}
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="text-error"
                  onClick={() => onRemove(attachmentUrls.filter((_, i) => i !== idx))}
                >
                  {removeLabel}
                </Button>
              </div>
            )
          })}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx"
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={isUploading}
        leftIcon={<Paperclip className="w-4 h-4" />}
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploadLabel}
      </Button>
    </div>
  )
}

async function uploadLeaseFileRaw(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return presigned.key
}

// ---------------------------------------------------------------------------
// EP85-P3-05: Tenant Vehicles card — shows active tenant vehicles for this lease
// and lets staff add a new vehicle pre-filled with tenant context.
// ---------------------------------------------------------------------------

interface AddVehicleFormData {
  license_plate: string
  vehicle_type: VehicleType | ''
  plate_text: string
  brand: string
  model: string
  color: string
}

interface TenantVehiclesCardProps {
  leaseId: number
  customerId: number
  propertyId: number | undefined
}

function TenantVehiclesCard({ leaseId, customerId, propertyId }: TenantVehiclesCardProps) {
  const { t } = useTranslation('leases')
  const { t: tp } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()

  const { data: vehiclesData, isLoading } = useVehiclesByLease(leaseId, 'tenant')
  const createVehicle = useCreateVehicle()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('leases')

  const [showAddModal, setShowAddModal] = useState(false)

  // Photo upload state for the add-vehicle modal
  const [frontPhotoKey, setFrontPhotoKey] = useState<string | null>(null)
  const [rearPhotoKey, setRearPhotoKey] = useState<string | null>(null)
  const [frontPhotoUploading, setFrontPhotoUploading] = useState(false)
  const [rearPhotoUploading, setRearPhotoUploading] = useState(false)
  const [frontPhotoError, setFrontPhotoError] = useState<string | undefined>()
  const [rearPhotoError, setRearPhotoError] = useState<string | undefined>()
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const frontPhotoInputRef = useRef<HTMLInputElement>(null)
  const rearPhotoInputRef = useRef<HTMLInputElement>(null)

  const handleFrontPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFrontPhotoUploading(true)
    setFrontPhotoError(undefined)
    try {
      const key = await uploadVehiclePhoto(file)
      setFrontPhotoKey(key)
    } catch {
      setFrontPhotoError(tp('vehicles.photo_upload_error'))
    } finally {
      setFrontPhotoUploading(false)
      if (frontPhotoInputRef.current) frontPhotoInputRef.current.value = ''
    }
  }

  const handleRearPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setRearPhotoUploading(true)
    setRearPhotoError(undefined)
    try {
      const key = await uploadVehiclePhoto(file)
      setRearPhotoKey(key)
    } catch {
      setRearPhotoError(tp('vehicles.photo_upload_error'))
    } finally {
      setRearPhotoUploading(false)
      if (rearPhotoInputRef.current) rearPhotoInputRef.current.value = ''
    }
  }

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<AddVehicleFormData>({
    defaultValues: {
      license_plate: '',
      vehicle_type: '',
      plate_text: '',
      brand: '',
      model: '',
      color: '',
    },
  })

  const vehicles = vehiclesData?.data ?? []

  const vehicleTypeOptions = [
    { value: '', label: `— ${tc('actions.select')} —` },
    { value: 'car', label: tp('vehicles.types.car') },
    { value: 'motorcycle', label: tp('vehicles.types.motorcycle') },
    { value: 'pickup', label: tp('vehicles.types.pickup') },
    { value: 'bicycle', label: tp('vehicles.types.bicycle') },
    { value: 'van', label: tp('vehicles.types.van') },
  ]

  const handleOpenModal = () => {
    resetForm()
    setFrontPhotoKey(null)
    setRearPhotoKey(null)
    setFrontPhotoError(undefined)
    setRearPhotoError(undefined)
    setSubmitAttempted(false)
    setShowAddModal(true)
  }

  const onAddVehicle = async (data: AddVehicleFormData) => {
    if (!propertyId || !data.vehicle_type) return

    // Both photos are required on create (backend validates presence on :create)
    setSubmitAttempted(true)
    let hasPhotoError = false
    if (!frontPhotoKey) {
      setFrontPhotoError(tp('vehicles.front_photo_required'))
      hasPhotoError = true
    }
    if (!rearPhotoKey) {
      setRearPhotoError(tp('vehicles.rear_photo_required'))
      hasPhotoError = true
    }
    if (hasPhotoError) return

    try {
      await createVehicle.mutateAsync({
        property_id: propertyId,
        license_plate: data.license_plate,
        vehicle_type: data.vehicle_type as VehicleType,
        plate_text: data.plate_text || undefined,
        brand: data.brand || undefined,
        model: data.model || undefined,
        color: data.color || undefined,
        customer_id: customerId,
        parker_type: 'tenant',
        lease_id: leaseId,
        front_photo_key: frontPhotoKey ?? undefined,
        rear_photo_key: rearPhotoKey ?? undefined,
      })
      // Invalidate vehicles list (the byLease query is under vehicleKeys.lists())
      // and also the lease detail so any embedded vehicle count refreshes.
      await queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: leaseKeys.detail(leaseId) })
      setShowAddModal(false)
      resetForm()
      setFrontPhotoKey(null)
      setRearPhotoKey(null)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (isLoading) {
    return (
      <Card>
        <div aria-label={tc('labels.refreshing')}>
          <Skeleton className="h-24" />
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card data-testid="tenant-vehicles-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Car className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {t('tenant_vehicles.title_with_count', { count: vehicles.length })}
            </h2>
          </div>
          {propertyId && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenModal}
              data-testid="add-vehicle-btn"
            >
              {t('tenant_vehicles.add_vehicle')}
            </Button>
          )}
        </div>

        {vehicles.length === 0 ? (
          <p className="text-sm text-base-content/60" data-testid="tenant-vehicles-empty">
            {t('tenant_vehicles.empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {vehicles.map((vehicle) => {
              const editHref = `/properties/${vehicle.property_id}/parking/vehicles/${vehicle.id}/edit`
              const photoUrl = vehicle.front_photo_key
                ? (vehicle.front_photo_key.startsWith('/api/') || vehicle.front_photo_key.startsWith('http')
                  ? vehicle.front_photo_key
                  : `${API_BASE}/api/images/${vehicle.front_photo_key}`)
                : null

              return (
                <div
                  key={vehicle.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-base-300"
                  data-testid={`tenant-vehicle-${vehicle.id}`}
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-12 rounded-lg overflow-hidden bg-base-200 flex-shrink-0 flex items-center justify-center">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={vehicle.plate_text ?? vehicle.license_plate}
                        className="w-full h-full object-cover"
                        data-testid={`vehicle-photo-${vehicle.id}`}
                      />
                    ) : (
                      <span className="text-xs text-base-content/40" data-testid={`vehicle-photo-placeholder-${vehicle.id}`}>
                        {t('tenant_vehicles.photo_placeholder')}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-semibold">{vehicle.plate_text ?? vehicle.license_plate}</div>
                    <div className="text-sm text-base-content/60 capitalize flex flex-wrap gap-x-2">
                      <span>{tp(`vehicles.types.${vehicle.vehicle_type}`)}</span>
                      {vehicle.color && <span>{vehicle.color}</span>}
                      {(vehicle.brand || vehicle.model) && (
                        <span>{[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}</span>
                      )}
                    </div>
                  </div>

                  {/* Edit link */}
                  <Button
                    as={Link}
                    to={editHref}
                    variant="ghost"
                    size="sm"
                    leftIcon={<Edit className="w-3 h-3" />}
                    aria-label={t('tenant_vehicles.edit_vehicle')}
                    data-testid={`edit-vehicle-link-${vehicle.id}`}
                  >
                    {t('tenant_vehicles.edit_vehicle')}
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Add Vehicle Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('tenant_vehicles.add_vehicle_modal_title')}
        size="md"
        footer={
          <div className="flex gap-2 w-full justify-end">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              form="add-tenant-vehicle-form"
              type="submit"
              loading={createVehicle.isPending}
            >
              {tc('actions.save')}
            </Button>
          </div>
        }
      >
        <form
          id="add-tenant-vehicle-form"
          onSubmit={handleSubmit(onAddVehicle)}
          className="space-y-4"
        >
          <Input
            label={tp('vehicles.plate_text_label')}
            placeholder={tp('vehicles.plate_text_placeholder')}
            error={errors.plate_text?.message}
            data-testid="add-vehicle-plate-text"
            {...register('plate_text')}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={tp('vehicles.license_plate')}
              placeholder={tp('vehicles.license_plate_placeholder')}
              error={errors.license_plate?.message}
              data-testid="add-vehicle-license-plate"
              {...register('license_plate', {
                required: tp('validation.license_plate_required'),
              })}
            />

            <Select
              label={tp('vehicles.vehicle_type')}
              error={errors.vehicle_type?.message}
              options={vehicleTypeOptions}
              data-testid="add-vehicle-type"
              {...register('vehicle_type', {
                required: tp('validation.vehicle_type_required'),
                validate: (v) => v !== '' || tp('validation.vehicle_type_required'),
              })}
            />

            <Input
              label={tp('vehicles.brand')}
              placeholder={tp('vehicles.brand')}
              data-testid="add-vehicle-brand"
              {...register('brand')}
            />

            <Input
              label={tp('vehicles.model')}
              placeholder={tp('vehicles.model')}
              data-testid="add-vehicle-model"
              {...register('model')}
            />

            <Input
              label={tp('vehicles.color')}
              placeholder={tp('vehicles.color')}
              data-testid="add-vehicle-color"
              {...register('color')}
            />
          </div>

          {/* Photos — required on create */}
          <div>
            <p className="text-sm font-medium mb-3">{tp('vehicles.photos_section_label')}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <VehiclePhotoUpload
                label={tp('vehicles.front_photo_label')}
                hint={tp('vehicles.front_photo_hint')}
                photoKey={frontPhotoKey}
                uploading={frontPhotoUploading}
                error={submitAttempted && !frontPhotoKey
                  ? (frontPhotoError ?? tp('vehicles.front_photo_required'))
                  : frontPhotoError}
                inputRef={frontPhotoInputRef}
                onFileChange={handleFrontPhotoChange}
                onRemove={() => setFrontPhotoKey(null)}
                data-testid="add-vehicle-front-photo"
              />

              <VehiclePhotoUpload
                label={tp('vehicles.rear_photo_label')}
                hint={tp('vehicles.rear_photo_hint')}
                photoKey={rearPhotoKey}
                uploading={rearPhotoUploading}
                error={submitAttempted && !rearPhotoKey
                  ? (rearPhotoError ?? tp('vehicles.rear_photo_required'))
                  : rearPhotoError}
                inputRef={rearPhotoInputRef}
                onFileChange={handleRearPhotoChange}
                onRemove={() => setRearPhotoKey(null)}
                data-testid="add-vehicle-rear-photo"
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

// ---------------------------------------------------------------------------
// EP85-P3-06: LPR Events card — shows last 10 LPR events for the tenant
// vehicles linked to this lease. Gated on "has any LPR events" as a fallback
// because no camera_count field is exposed on the property payload. If no tenant
// vehicles are registered, the query is skipped and the card is not rendered.
// ---------------------------------------------------------------------------

const lprApiBase = import.meta.env.VITE_API_URL || ''

function lprThumbUrl(key: string | null | undefined): string | null {
  if (!key) return null
  return key.startsWith('/api/images/')
    ? `${lprApiBase}${key}`
    : `${lprApiBase}/api/images/${key}`
}

interface LprEventsCardProps {
  leaseId: number
  propertyId: number | undefined
}

function LprEventsCard({ leaseId, propertyId }: LprEventsCardProps) {
  const { t } = useTranslation('leases')
  const { t: tp } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const formatDateTime = useDateTimeFormatter()

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)

  // Reuse the same query TenantVehiclesCard uses — TanStack Query deduplicates.
  const { data: vehiclesData, isLoading: isVehiclesLoading } = useVehiclesByLease(leaseId, 'tenant')
  const vehicleIds = (vehiclesData?.data ?? []).map((v) => v.id)

  // Skip the LPR query when there are no tenant vehicles — avoids a pointless request.
  const hasVehicles = vehicleIds.length > 0
  const { data, isLoading: isEventsLoading } = useLprEvents({
    vehicle_ids: vehicleIds,
    limit: 10,
    enabled: hasVehicles && !isVehiclesLoading,
  })
  const isLoading = isVehiclesLoading || (hasVehicles && isEventsLoading)

  const events = data?.data ?? []
  const hasEvents = events.length > 0

  // Gate: no vehicles → don't render anything
  if (!hasVehicles) return null

  // Gate: finished loading but no events ever → don't render the card
  // (property has no LPR cameras or no activity)
  if (!isLoading && !hasEvents) return null

  const selectedEvent = events.find((e) => e.id === selectedEventId) ?? null

  // Ordered pending event ids for drawer ↑/↓ navigation across this mini-list
  const pendingEventIds = events
    .filter((e) => e.verification_status === 'pending')
    .map((e) => e.id)

  function handleNavigate(direction: 'up' | 'down') {
    if (!selectedEventId) return
    const idx = pendingEventIds.indexOf(selectedEventId)
    if (direction === 'down') {
      const nextId = pendingEventIds[idx + 1] ?? pendingEventIds[0] ?? null
      setSelectedEventId(nextId)
    } else {
      const prevId =
        pendingEventIds[idx - 1] ?? pendingEventIds[pendingEventIds.length - 1] ?? null
      setSelectedEventId(prevId)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <div aria-label={tc('labels.refreshing')}>
          <Skeleton className="h-24" />
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card data-testid="lpr-events-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">{t('lpr_events_card.title')}</h2>
          </div>
          {propertyId && (
            <Link
              to={`/properties/${propertyId}/parking/lpr`}
              className="text-sm text-primary hover:underline"
              data-testid="lpr-log-link"
            >
              {t('lpr_events_card.open_log')}
            </Link>
          )}
        </div>

        {events.length === 0 ? (
          <p className="text-sm text-base-content/60" data-testid="lpr-events-empty">
            {t('lpr_events_card.empty')}
          </p>
        ) : (
          <div className="space-y-2">
            {events.map((event) => {
              const thumb = lprThumbUrl(event.front_thumb_key)
              const topCandidateId = event.ml_candidate_vehicle_ids?.[0]
              const isSelected = event.id === selectedEventId

              return (
                <div
                  key={event.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-base-200'
                      : 'border-base-300 hover:bg-base-100'
                  }`}
                  onClick={() => setSelectedEventId((prev) => (prev === event.id ? null : event.id))}
                  role="button"
                  aria-selected={isSelected}
                  data-testid={`lpr-event-row-${event.id}`}
                >
                  {/* Thumbnail */}
                  <div className="w-14 h-10 rounded overflow-hidden bg-base-200 flex-shrink-0 flex items-center justify-center">
                    {thumb && event.thumb_fetch_status !== 'failed' ? (
                      <img
                        src={thumb}
                        alt={tp('lpr.thumbnail_alt')}
                        className="w-full h-full object-cover"
                        data-testid={`lpr-thumb-${event.id}`}
                      />
                    ) : (
                      <ScanLine
                        className="w-5 h-5 text-base-content/30"
                        aria-label={tp('lpr.thumbnail_unavailable')}
                        data-testid={`lpr-thumb-placeholder-${event.id}`}
                      />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-medium">
                        {topCandidateId != null ? `#${topCandidateId}` : '—'}
                      </span>
                      {event.ml_re_id_score != null && (
                        <span className="text-xs text-base-content/50">
                          ({(event.ml_re_id_score * 100).toFixed(0)}%)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-base-content/60 flex flex-wrap gap-x-2 mt-0.5">
                      <span>{formatDateTime(event.captured_at)}</span>
                      {event.direction && (
                        <span>{tp(`lpr.directions.${event.direction}`)}</span>
                      )}
                    </div>
                  </div>

                  {/* Verification status badge */}
                  <Badge
                    variant={
                      event.verification_status === 'verified_success'
                        ? 'success'
                        : event.verification_status === 'verified_failed'
                          ? 'error'
                          : 'warning'
                    }
                  >
                    {tp(`lpr.verification_statuses.${event.verification_status}`)}
                  </Badge>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Verification drawer — renders when a row is selected */}
      {selectedEvent && (
        <LprVerificationDrawer
          event={selectedEvent}
          pendingEventIds={pendingEventIds}
          onClose={() => setSelectedEventId(null)}
          onNavigate={handleNavigate}
        />
      )}
    </>
  )
}

/**
 * EPV-11: Inline vehicle reassignment section — only renders when lease.status === 'ended'
 * and there are vehicles still linked to the lease.
 *
 * All 3 transitions (archive / reassign / convert-to-external) hit the SAME
 * PATCH /api/vehicles/:id with different payload shapes per the backend design.
 */
type VehicleAction = 'reassign' | 'convert_to_external'

interface VehicleReassignSectionProps {
  leaseId: number
}

function VehicleReassignSection({ leaseId }: VehicleReassignSectionProps) {
  const { t } = useTranslation('leases')
  const { t: tp } = useTranslation('parking')
  const { t: tc } = useTranslation('common')

  const { data: vehiclesData, isLoading } = useVehiclesByLease(leaseId)
  const updateVehicle = useUpdateVehicle()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('leases')

  // Active leases for reassign dropdown
  const { data: activeLeasesData } = useLeases(1, 'active,pending_start')
  const activeLeases = activeLeasesData?.data || []

  // Customers for convert-to-external dropdown
  const { data: customersData } = useCustomers(1)
  const customers = customersData?.data || []

  // Inline expansion state: only one row can be expanded at a time
  const [expandedVehicleId, setExpandedVehicleId] = useState<number | null>(null)
  const [expandedAction, setExpandedAction] = useState<VehicleAction | null>(null)
  // Form values for inline expansions
  const [selectedLeaseId, setSelectedLeaseId] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState('')

  // Archive confirmation dialog
  const [archiveVehicleId, setArchiveVehicleId] = useState<number | null>(null)

  const vehicles = vehiclesData?.data || []

  if (isLoading) {
    return (
      <Card>
        <div aria-label={tc('labels.refreshing')}>
          <Skeleton className="h-24" />
        </div>
      </Card>
    )
  }

  if (vehicles.length === 0) return null

  const openExpansion = (vehicleId: number, action: VehicleAction) => {
    setExpandedVehicleId(vehicleId)
    setExpandedAction(action)
    setSelectedLeaseId('')
    setSelectedCustomerId('')
  }

  const closeExpansion = () => {
    setExpandedVehicleId(null)
    setExpandedAction(null)
    setSelectedLeaseId('')
    setSelectedCustomerId('')
  }

  const handleArchive = async (vehicleId: number) => {
    try {
      await updateVehicle.mutateAsync({ id: vehicleId, parker_type: 'archived' })
      setArchiveVehicleId(null)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleReassign = async (vehicleId: number) => {
    if (!selectedLeaseId) return
    try {
      await updateVehicle.mutateAsync({ id: vehicleId, lease_id: Number(selectedLeaseId) })
      closeExpansion()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleConvertToExternal = async (vehicleId: number) => {
    if (!selectedCustomerId) return
    try {
      await updateVehicle.mutateAsync({
        id: vehicleId,
        parker_type: 'external',
        customer_id: Number(selectedCustomerId),
      })
      closeExpansion()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Car className="w-5 h-5 text-warning" />
        <h2 className="text-lg font-semibold">{t('reassign_vehicles.title')}</h2>
      </div>
      <p className="text-sm text-base-content/60 mb-4">{t('reassign_vehicles.description')}</p>

      <div className="space-y-3">
        {vehicles.map((vehicle) => {
          const isExpanded = expandedVehicleId === vehicle.id
          const activePermitBadge = vehicle.active_permits && vehicle.active_permits.length > 0
            ? vehicle.active_permits[0].permit_type
            : null

          return (
            <div
              key={vehicle.id}
              className="rounded-lg border border-base-300 overflow-hidden"
              data-testid={`vehicle-row-${vehicle.id}`}
            >
              {/* Main row */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-3">
                  <div className="font-mono font-semibold">{vehicle.license_plate}</div>
                  <div className="text-sm text-base-content/60 capitalize">{tp(`vehicles.types.${vehicle.vehicle_type}`)}</div>
                  {activePermitBadge && (
                    <Badge size="sm" outline>
                      {tp(`permits.types.${activePermitBadge}`)}
                    </Badge>
                  )}
                </div>

                {/* Action buttons — hidden when this row is expanded */}
                {!isExpanded && (
                  <div className="flex gap-2">
                    <Button
                      variant="error"
                      outline
                      size="sm"
                      onClick={() => setArchiveVehicleId(vehicle.id)}
                      data-testid={`archive-btn-${vehicle.id}`}
                    >
                      {t('reassign_vehicles.archive')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openExpansion(vehicle.id, 'reassign')}
                      data-testid={`reassign-btn-${vehicle.id}`}
                    >
                      {t('reassign_vehicles.reassign')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openExpansion(vehicle.id, 'convert_to_external')}
                      data-testid={`convert-btn-${vehicle.id}`}
                    >
                      {t('reassign_vehicles.convert_to_external')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Inline expansion */}
              {isExpanded && expandedAction === 'reassign' && (
                <div className="border-t border-base-300 bg-base-200 p-3 space-y-3" data-testid={`reassign-expansion-${vehicle.id}`}>
                  <p className="text-sm font-medium">{t('reassign_vehicles.reassign_to_lease')}</p>
                  <Select
                    className="w-full max-w-sm"
                    options={activeLeases.map((l) => ({
                      value: String(l.id),
                      label: `${l.lease_code} · ${l.unit?.name ?? `#${l.unit_id}`} · ${l.customer?.name ?? `#${l.customer_id}`}`,
                    }))}
                    value={selectedLeaseId}
                    onChange={(e) => setSelectedLeaseId(e.target.value)}
                    placeholder={`— ${tc('actions.select')} —`}
                    data-testid="reassign-lease-select"
                  />
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={closeExpansion}>
                      {tc('actions.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      loading={updateVehicle.isPending}
                      disabled={!selectedLeaseId || updateVehicle.isPending}
                      onClick={() => handleReassign(vehicle.id)}
                      data-testid="reassign-confirm-btn"
                    >
                      {t('reassign_vehicles.confirm')}
                    </Button>
                  </div>
                </div>
              )}

              {isExpanded && expandedAction === 'convert_to_external' && (
                <div className="border-t border-base-300 bg-base-200 p-3 space-y-3" data-testid={`convert-expansion-${vehicle.id}`}>
                  <p className="text-sm font-medium">{t('reassign_vehicles.assign_customer')}</p>
                  <Select
                    className="w-full max-w-sm"
                    options={customers.map((c) => ({
                      value: String(c.id),
                      label: c.last_name
                        ? `${c.first_name ?? ''} ${c.last_name}`.trim()
                        : (c.first_name ?? c.phone ?? `#${c.id}`),
                    }))}
                    value={selectedCustomerId}
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    placeholder={`— ${tc('actions.select')} —`}
                    data-testid="convert-customer-select"
                  />
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={closeExpansion}>
                      {tc('actions.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      loading={updateVehicle.isPending}
                      disabled={!selectedCustomerId || updateVehicle.isPending}
                      onClick={() => handleConvertToExternal(vehicle.id)}
                      data-testid="convert-confirm-btn"
                    >
                      {t('reassign_vehicles.confirm')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Archive confirmation dialog */}
      <ConfirmDialog
        isOpen={archiveVehicleId !== null}
        onClose={() => setArchiveVehicleId(null)}
        onConfirm={() => archiveVehicleId !== null && handleArchive(archiveVehicleId)}
        title={t('reassign_vehicles.archive')}
        message={t('reassign_vehicles.confirm_archive')}
        confirmLabel={t('reassign_vehicles.archive')}
        variant="error"
        loading={updateVehicle.isPending}
      />
    </Card>
  )
}

// ---------------------------------------------------------------------------
// EP-130 (T130-011): Deposit panel — shows the collection state of the
// lease's deposit invoice. Renders only for non-condo leases with a
// deposit > 0 (caller gates). Deductions/return rows (settlement, T130-012)
// are a separate sibling section, not built here.
// ---------------------------------------------------------------------------

interface DepositPanelCardProps {
  summary: DepositSummary
  /** EP-149 T149-079 (MON-7 C): opens the "record deposit held" modal. Omit
   *  the button entirely by leaving this undefined (e.g. no caller wired). */
  onRecordHeld?: () => void
  /** Mirrors the Bill-deposit action's own role gate (EP-129 D-3). */
  canRecordHeld?: boolean
}

function DepositPanelCard({ summary, onRecordHeld, canRecordHeld }: DepositPanelCardProps) {
  const { t } = useTranslation('leases')

  const invoice = summary.deposit_invoice
  const booking = summary.booking_invoice
  const heldBeforeFlowrent = summary.held_before_flowrent
  // Only a PAID booking fee counts, which is what keeps this clear of EP-130
  // Q-5: an unpaid or part-paid invoice contributes 0 and surfaces no partial.
  // The deposit invoice is deliberately not summed here — it carries no amount
  // in the payload, and once it IS paid the deposit reads collected and the
  // badge says Full, so there is no shortfall left to describe. T149-075
  // (MON-7 A) adds a third, already-fully-known source on top: a deposit
  // held before FlowRent, recorded directly on the lease.
  const collectedSoFar = (booking?.paid ? booking.amount : 0) + (heldBeforeFlowrent?.amount ?? 0)
  const hasBooking = booking != null
  const hasHeldBeforeFlowrent = heldBeforeFlowrent != null

  // EP-136 (T136-025) — once a paid booking fee alone reaches the deposit
  // target, `deposit_invoice` stays null forever: the remainder is 0, so
  // Bill-deposit 422s with `deposit_already_covered`
  // (DepositSettlementCalculator#deposit_remainder) and no deposit invoice
  // is ever created. Rendering the usual "not yet billed" wording in that
  // state would read as still-owed money that does not exist, so the
  // remainder row is omitted outright rather than shown as unbilled.
  // T149-075 widens this the same way: a deposit held before FlowRent can
  // reach the target with no booking at all, and the row must be omitted
  // then too, not just when a booking is the source.
  const depositFullyCoveredWithoutInvoice = !invoice && summary.collected
  const showRemainderRow = !depositFullyCoveredWithoutInvoice

  // EP-149 T149-079 (MON-7 C): mirrors the Bill-deposit action's own guard
  // (owner ruling 2026-09-23), visible only while there is money still to
  // record: not already collected, and neither a deposit nor a booking
  // invoice exists yet on the chain (once either exists, the ordinary
  // invoice/payment flow is the record of what was collected).
  const canShowRecordHeldAction =
    onRecordHeld != null &&
    canRecordHeld === true &&
    !summary.collected &&
    invoice == null &&
    booking == null

  return (
    <Card data-testid="deposit-panel-card">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{t('deposit_panel.title')}</h2>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <span className="text-sm text-base-content/60">{t('deposit_panel.amount_label')}</span>
          <span className="font-medium">{formatBaht(summary.amount)}</span>
        </div>

        {/* EP-136 (T136-025) — booking-fee row, only when a booking exists on
            the lease chain. `booking_invoice` is populated for a `booking_fee`
            line only, never a `reservation_fee` one — that fee is revenue,
            not deposit money, and correctly never surfaces here. */}
        {booking && (
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-sm text-base-content/60">{t('deposit_panel.booking_fee_label')}</span>
            <div className="flex items-center gap-2 flex-wrap" data-testid="deposit-panel-booking-fee">
              <Link to={`/invoices/${booking.id}`} className="text-primary hover:underline font-medium">
                {booking.invoice_number}
              </Link>
              <span className="font-medium">{formatBaht(booking.amount)}</span>
              <Badge variant={booking.paid ? 'success' : 'warning'}>
                {booking.paid ? t('deposit_panel.status_paid') : t('deposit_panel.status_pending')}
              </Badge>
            </div>
          </div>
        )}

        {/* Relabelled "remainder" once a booking exists (the deposit invoice
            then bills lease.deposit − paid booking fee, not the full amount);
            otherwise byte-identical to the pre-EP-136 "billed" row. */}
        {showRemainderRow && (
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-sm text-base-content/60">
              {hasBooking ? t('deposit_panel.remainder_label') : t('deposit_panel.billed_label')}
            </span>
            {invoice ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="deposit-panel-billed">
                <Link to={`/invoices/${invoice.id}`} className="text-primary hover:underline font-medium">
                  {invoice.invoice_number}
                </Link>
                <Badge variant={invoice.paid ? 'success' : 'warning'}>
                  {invoice.paid ? t('deposit_panel.status_paid') : t('deposit_panel.status_pending')}
                </Badge>
              </div>
            ) : (
              <span className="text-base-content/60" data-testid="deposit-panel-unbilled">
                {t('deposit_panel.unbilled')}
              </span>
            )}
          </div>
        )}

        {/* EP-136 (T136-025): total-collected row, only when a booking or a
            held-before-FlowRent amount exists (a lease with neither stays
            byte-identical to the pre-EP-136 panel). `collected` is the
            server-derived boolean (DepositSettlementCalculator#collected?,
            itself a sum of the paid deposit + booking-fee lines + the held
            amount); the FE never recomputes it, it only picks the badge. */}
        {(hasBooking || hasHeldBeforeFlowrent) && (
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-sm text-base-content/60">{t('deposit_panel.total_collected_label')}</span>
            <span data-testid="deposit-panel-total-collected" className="flex items-center gap-2">
              {/* EP-130 Q-5 says no PARTIAL amount is ever surfaced, and that
                  still holds: this sums only invoices the server reports as
                  fully PAID, so a part-paid invoice contributes nothing and
                  leaks nothing. Q-5 was guarding against treating one invoice
                  as incrementally payable ("no top-ups"); a booking fee is a
                  discrete fully-paid invoice in a designed two-part
                  collection, and both figures are already on screen directly
                  above. Summing server-computed line amounts for a footer is
                  the one arithmetic the frontend is allowed. */}
              {!summary.collected && (
                <span className="text-sm tabular-nums" data-testid="deposit-panel-collected-so-far">
                  {t('deposit_panel.collected_of', {
                    collected: formatBaht(collectedSoFar),
                    total: formatBaht(summary.amount),
                  })}
                </span>
              )}
              <Badge variant={summary.collected ? 'success' : 'warning'}>
                {summary.collected ? t('deposit_panel.status_full') : t('deposit_panel.status_short')}
              </Badge>
            </span>
          </div>
        )}
      </div>

      {/* EP-149 T149-079 (MON-7 C): "record deposit held before FlowRent"
          disappears once collected or once a deposit/booking invoice exists,
          matching the Bill-deposit action's own guard. */}
      {canShowRecordHeldAction && (
        <div className="flex justify-end mt-4 pt-3 border-t border-base-200">
          <Button
            variant="outline"
            size="sm"
            onClick={onRecordHeld}
            data-testid="lease-record-deposit-held"
          >
            {t('deposit_panel.record_held_before_flowrent')}
          </Button>
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// EP-130 (T130-012): Settlement card — sibling of DepositPanelCard, renders
// once the lease has ended_at (any date, no <= today guard — Q-3) for a
// non-condo lease with a positive deposit. Lists move-out deductions (linked
// work orders + lease-linked expenses, deposit_return excluded from that
// list per the API contract) and offers Return-deposit / Bill-shortfall /
// deduction-recording actions that deep-link into the Expense, Work-order and
// Invoice create forms via query params.
//
// Deep-link contracts this card emits, each consumed at the other end:
//   - /expenses/new?lease_id&category=deposit_return&amount&property_id
//     (ExpenseFormPage pre-fills and locks the form from these)
//   - /expenses/new?lease_id
//     (the "ordinary, non-deposit_return category" deduction deep-link)
//   - /work-orders/new?lease_id
//     (WorkOrderFormPage pre-selects and locks the lease picker)
//   - /invoices/new?lease_id&item_type=other&description&amount
//     (InvoiceFormPage resolves these into InvoiceFields' prefillItem, which
//     appends the shortfall row alongside the engine-priced ones)
// ---------------------------------------------------------------------------

interface SettlementCardProps {
  leaseId: number
  summary: DepositSummary
  propertyId: number | undefined
  cmmsAvailable: boolean
}

function SettlementCard({ leaseId, summary, propertyId, cmmsAvailable }: SettlementCardProps) {
  const { t } = useTranslation('leases')

  const header = (badge?: React.ReactNode) => (
    <div className="flex items-center gap-2">
      <HandCoins className="w-5 h-5 text-primary" />
      <h2 className="text-lg font-semibold">{t('settlement_card.title')}</h2>
      {badge}
    </div>
  )

  // Never collected — nothing to settle, no actions offered.
  if (!summary.collected) {
    return (
      <Card data-testid="settlement-card">
        <div className="mb-4">{header()}</div>
        <EmptyState
          title={t('settlement_card.never_collected_title')}
          description={t('settlement_card.never_collected_description')}
        />
      </Card>
    )
  }

  const collectedAmount = summary.amount
  const isSettled = summary.returned > 0
  const isShortfall = !isSettled && summary.deductions.total > collectedAmount
  const excess = isShortfall ? summary.deductions.total - collectedAmount : 0
  // CALC-NOTE: `returnable` is the API's stable prefill (collected minus
  // deductions, never reduced by `returned`); `remaining` is FE-computed so
  // it correctly reads 0 once the return has actually been recorded.
  const remaining = Math.max(summary.returnable - summary.returned, 0)
  const isCleared = isSettled && remaining <= 0

  const hasDeductions =
    summary.deductions.work_orders.length > 0 || summary.deductions.expenses.length > 0

  const returnDepositHref =
    `/expenses/new?lease_id=${leaseId}&category=deposit_return&amount=${summary.returnable}` +
    (propertyId != null ? `&property_id=${propertyId}` : '')
  const recordDeductionHref = `/expenses/new?lease_id=${leaseId}`
  const addWorkOrderHref = `/work-orders/new?lease_id=${leaseId}`
  const shortfallDescription = t('settlement_card.shortfall_line_description')
  const billShortfallHref =
    `/invoices/new?lease_id=${leaseId}&item_type=other` +
    `&description=${encodeURIComponent(shortfallDescription)}&amount=${excess}`

  return (
    <Card data-testid="settlement-card">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        {header(
          isCleared && (
            <span data-testid="settlement-cleared-badge">
              <Badge variant="success">{t('settlement_card.cleared_badge')}</Badge>
            </span>
          )
        )}
        {!isSettled && (
          <div className="flex flex-col sm:flex-row justify-end gap-2">
            {isShortfall ? (
              <Button
                as={Link}
                to={billShortfallHref}
                variant="warning"
                data-testid="settlement-bill-shortfall"
              >
                {t('settlement_card.bill_shortfall')}
              </Button>
            ) : (
              <Button as={Link} to={returnDepositHref} data-testid="settlement-return-deposit">
                {t('settlement_card.return_deposit')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Collected */}
        <div className="flex justify-between items-center flex-wrap gap-2">
          <span className="text-sm text-base-content/60">{t('settlement_card.collected_label')}</span>
          <div className="flex items-center gap-2 flex-wrap">
            {summary.deposit_invoice && (
              <Link
                to={`/invoices/${summary.deposit_invoice.id}`}
                className="text-primary hover:underline"
              >
                {summary.deposit_invoice.invoice_number}
              </Link>
            )}
            <span className="font-medium">{formatBaht(collectedAmount)}</span>
          </div>
        </div>

        {/* Deductions */}
        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <span className="text-sm text-base-content/60">{t('settlement_card.deductions_label')}</span>
            {!isSettled && (
              cmmsAvailable ? (
                <Button
                  as={Link}
                  to={addWorkOrderHref}
                  variant="ghost"
                  size="sm"
                  data-testid="settlement-add-work-order"
                >
                  + {t('settlement_card.add_work_order')}
                </Button>
              ) : (
                <Button
                  as={Link}
                  to={recordDeductionHref}
                  variant="ghost"
                  size="sm"
                  data-testid="settlement-record-deduction"
                >
                  + {t('settlement_card.record_deduction')}
                </Button>
              )
            )}
          </div>
          {!hasDeductions ? (
            <p className="text-sm text-base-content/60" data-testid="settlement-no-deductions">
              {t('settlement_card.no_deductions')}
            </p>
          ) : (
            <div className="space-y-2">
              {summary.deductions.work_orders.map((wo) => (
                <div
                  key={`wo-${wo.id}`}
                  className="flex justify-between items-center flex-wrap gap-2 text-sm"
                  data-testid={`settlement-deduction-wo-${wo.id}`}
                >
                  <Link to={`/work-orders/${wo.id}`} className="text-primary hover:underline">
                    {wo.title}
                  </Link>
                  <span>-{formatBaht(wo.actual_cost ?? 0)}</span>
                </div>
              ))}
              {summary.deductions.expenses.map((exp) => (
                <div
                  key={`exp-${exp.id}`}
                  className="flex justify-between items-center flex-wrap gap-2 text-sm"
                  data-testid={`settlement-deduction-exp-${exp.id}`}
                >
                  <Link to={`/expenses/${exp.id}`} className="text-primary hover:underline">
                    {exp.description || t('settlement_card.expense_fallback_label')}
                  </Link>
                  <span>-{formatBaht(exp.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shortfall / returnable / settled */}
        {isShortfall ? (
          <Alert variant="warning" data-testid="settlement-shortfall-alert">
            {t('settlement_card.shortfall_message', { amount: formatBaht(excess) })}
          </Alert>
        ) : isSettled ? (
          <>
            <div className="flex justify-between items-center flex-wrap gap-2">
              <span className="text-sm text-base-content/60">{t('settlement_card.returned_label')}</span>
              <span className="font-medium" data-testid="settlement-returned-amount">
                {formatBaht(summary.returned)}
              </span>
            </div>
            <div className="flex justify-between items-center flex-wrap gap-2">
              <span className="text-sm text-base-content/60">{t('settlement_card.remaining_label')}</span>
              <span className="font-medium" data-testid="settlement-remaining-amount">
                {formatBaht(remaining)}
              </span>
            </div>
          </>
        ) : (
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-sm text-base-content/60">{t('settlement_card.returnable_label')}</span>
            <span className="font-medium" data-testid="settlement-returnable-amount">
              {formatBaht(summary.returnable)}
            </span>
          </div>
        )}
      </div>
    </Card>
  )
}

export function LeaseDetailPage() {
  const { t, i18n } = useTranslation('leases')
  const { t: tc } = useTranslation('common')
  const { t: tCon } = useTranslation('contracts')
  const { id } = useParams<{ id: string }>()
  const leaseId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()

  // EP-59 V6 — on condominium accounts, the detail page shows an
  // OwnerContract: no Sign now button, no deposit field, ownership
  // record header. Account-level branching (consistent with V3/V4/V5)
  // rather than per-record `lease.contract_type` branching, because
  // the account_type is the routing source of truth (CLAUDE.md item
  // 34) and is already cached.
  const { data: accountSettings } = useAccountSettings()
  // QA B-048 — gate the LPR events card on the account setting (admin-managed, EP-53).
  const lprEnabled = accountSettings?.settings?.lpr_enabled ?? false
  const formatDate = useDateFormatter()
  const formatDateTime = useDateTimeFormatter()
  const { timezone, format: dateFormat, calendar } = useLocalizationConfig()
  const isCondominium = accountSettings?.account_type === 'condominium'
  // EP-130 (T130-012) — settlement card's deduction action mirrors CMMS
  // availability the same way the sidebar does (Sidebar.tsx `showCmms`):
  // dual-gate (Unleash flag AND the account setting), because the
  // /work-orders routes themselves are registered behind the SAME flag in
  // App.tsx — a setting-only gate would deep-link to a route that doesn't exist.
  const featureFlags = useFeatureFlags()
  const cmmsAvailable = featureFlags.cmms && (accountSettings?.settings?.cmms_enabled ?? false)

  const { data: lease, isLoading, isFetching } = useLease(leaseId)
  const updateLease = useUpdateLease()
  const endLease = useEndLease()
  const renewLease = useRenewLease()
  const deleteLease = useDeleteLease()
  const generateSignLinks = useGenerateSignLinks()
  const createDepositInvoice = useCreateDepositInvoice()
  const recordDepositHeld = useRecordDepositHeld()
  const regenerateSignLink = useRegenerateSignLink()
  const { data: signatures } = useLeaseSignatures(leaseId)
  const { data: templatesData } = useDocumentTemplates(1, 'lease')

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [confirmRenew, setConfirmRenew] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  // Does the stored PDF actually exist? Decided up front so the action can be a
  // plain anchor in the normal case: the blob-download path
  // (createObjectURL + <a download>.click()) is a silent no-op inside the
  // Capacitor WKWebView, which is why "download PDF" did nothing on mobile.
  // An anchor with target=_blank IS handled natively (Capacitor hands it to the
  // system browser), so the healthy path uses one and only the missing-blob
  // case keeps the fetch-then-rebuild handler.
  //
  // Fail-open: ONLY a definite 404 flips this. Offline, a 5xx or a CORS problem
  // leaves the rebuild path in place rather than asserting the PDF is gone —
  // rebuilding over a signed lease is unrecoverable.
  const [storedPdfMissing, setStoredPdfMissing] = useState(false)

  const storedPdfKey = lease?.pdf_key
  useEffect(() => {
    if (!storedPdfKey) return
    let cancelled = false
    setStoredPdfMissing(false)
    resolveImageUrl(storedPdfKey).catch((err) => {
      if (!cancelled && err instanceof ImageNotFoundError) setStoredPdfMissing(true)
    })
    return () => {
      cancelled = true
    }
  }, [storedPdfKey])
  const [showSignModal, setShowSignModal] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  // Sign modal form state
  const [ownerName, setOwnerName] = useState('')
  const [witness1, setWitness1] = useState('')
  const [witness2, setWitness2] = useState('')

  // EP-149 T149-079 (MON-7 C): record deposit held before FlowRent modal state
  const [showDepositHeldModal, setShowDepositHeldModal] = useState(false)
  const [depositHeldAmount, setDepositHeldAmount] = useState('')
  const [depositHeldDate, setDepositHeldDate] = useState('')

  // Regenerate modal state
  const [regenSig, setRegenSig] = useState<{ id: number; signer_name: string } | null>(null)
  const [regenName, setRegenName] = useState('')

  // Attachment upload state — null means use server data
  const [localImageUrls, setLocalImageUrls] = useState<string[] | null>(null)
  const [localAttachmentUrls, setLocalAttachmentUrls] = useState<string[] | null>(null)

  const imageUrls = localImageUrls ?? (lease?.image_urls ?? [])
  const attachmentUrls = localAttachmentUrls ?? (lease?.attachment_urls ?? [])
  // EP-160 S3: a FlowRent support session gets no PDF, image or attachment
  // keys (`<key>_present` only), so those areas read "[ ซ่อน ]" and nothing
  // that would write them is offered: an upload would replace the stored list
  // with what this tab holds, and generating a PDF would replace a signed one.
  const pdfHidden = removedInSession(lease, 'pdf_key')
  const imagesHidden = removedInSession(lease, 'image_urls')
  const attachmentsHidden = removedInSession(lease, 'attachment_urls')

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  // EP-129 D-3: the accountant reads leases but never changes them. Every lease
  // lifecycle route authorizes PropertyPolicy#update? (staff_or_higher?), which
  // already denies the accountant a 403 — this mirrors that server rule so the
  // role is not offered buttons that cannot work. Byte-identical for the other
  // three roles: staff/admin/owner all satisfy it.
  const canMutateLease = user?.role === 'staff' || isAdminOrOwner
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('leases')

  // Signature panel role label — reuses the same default-name copy shown in
  // the sign-links modal (sign_modal.default_*_name) rather than a raw
  // underscore-replaced signer_role string. Falls back to the raw role for
  // any future value the backend adds ahead of a translation key.
  const signerRoleLabels: Record<string, string> = {
    tenant: t('sign_modal.default_tenant_name'),
    owner: t('sign_modal.default_owner_name'),
    witness_1: t('sign_modal.default_witness_1_name'),
    witness_2: t('sign_modal.default_witness_2_name'),
  }
  const signerRoleLabel = (role: string) => signerRoleLabels[role] ?? role.replace(/_/g, ' ')

  const handleDelete = async () => {
    try {
      await deleteLease.mutateAsync(leaseId)
      navigate('/leases')
    } catch (err) {
      setConfirmDelete(false)
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleBillDeposit = async () => {
    try {
      await createDepositInvoice.mutateAsync(leaseId)
      addToast({ type: 'success', message: t('deposit_panel.bill_success') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // EP-149 T149-079 (MON-7 C): amount prefills from the deposit target (the
  // modal only ever shows while nothing has been billed against it yet, per
  // DepositPanelCard's own guard, so there is no partial remainder to derive).
  const openDepositHeldModal = () => {
    setDepositHeldAmount(String(lease?.deposit_summary?.amount ?? lease?.deposit ?? ''))
    setDepositHeldDate(localToday(timezone))
    setShowDepositHeldModal(true)
  }

  const handleRecordDepositHeld = async () => {
    try {
      await recordDepositHeld.mutateAsync({
        leaseId,
        amount: Number(depositHeldAmount),
        date: depositHeldDate,
      })
      addToast({ type: 'success', message: t('deposit_panel.held_before_flowrent_success') })
      setShowDepositHeldModal(false)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleActivate = async () => {
    await updateLease.mutateAsync({ id: leaseId, status: 'active' })
  }

  const handleEnd = async () => {
    try {
      const { refund_expense } = await endLease.mutateAsync({ leaseId })
      setConfirmEnd(false)
      if (refund_expense) {
        addToast({
          type: 'success',
          message: t('messages.end_success_with_expense'),
          action: { label: tc('actions.view'), to: `/expenses/${refund_expense.id}` },
        })
      } else {
        addToast({ type: 'success', message: t('messages.end_success') })
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // EP-162 item 11 follow-up 1: Renew follows the server's `renewable`, and
  // opens the new draft lease once it exists.
  const handleRenew = async () => {
    try {
      const renewal = await renewLease.mutateAsync(leaseId)
      setConfirmRenew(false)
      addToast({ type: 'success', message: t('messages.renew_success') })
      navigate(`/leases/${renewal.id}`)
    } catch (err) {
      setConfirmRenew(false)
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const handleGenerateSignLinks = async () => {
    if (!lease) return
    const signers: Array<{ role: string; name: string }> = [
      { role: 'tenant', name: lease.customer?.name || t('sign_modal.default_tenant_name') },
      { role: 'owner', name: ownerName.trim() || t('sign_modal.default_owner_name') },
      { role: 'witness_1', name: witness1.trim() || t('sign_modal.default_witness_1_name') },
      { role: 'witness_2', name: witness2.trim() || t('sign_modal.default_witness_2_name') },
    ]
    await generateSignLinks.mutateAsync({ leaseId, signers })
    setShowSignModal(false)
  }

  const handleCopyLink = async (signature: { id: number; sign_url: string }) => {
    await navigator.clipboard.writeText(`${window.location.origin}${signature.sign_url}`)
    setCopiedId(signature.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const openRegenModal = (sig: { id: number; signer_name: string }) => {
    setRegenSig(sig)
    setRegenName(sig.signer_name)
  }

  const handleRegenerate = async () => {
    if (!regenSig) return
    await regenerateSignLink.mutateAsync({
      leaseId,
      signatureId: regenSig.id,
      signer_name: regenName.trim() || undefined,
    })
    setRegenSig(null)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge>{t('status.draft')}</Badge>
      case 'pending_signatures':
        return <Badge variant="warning">{t('status.pending_signatures')}</Badge>
      case 'pending_start':
        return <Badge variant="info">{t('status.pending_start')}</Badge>
      case 'active':
        return <Badge variant="success">{t('status.active')}</Badge>
      case 'ended':
        return <Badge variant="ghost">{t('status.ended')}</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-48" />
          </Card>
        </div>
    )
  }

  if (!lease) {
    return (
        <Card>
          <EmptyState
            title={t('messages.not_found')}
            action={<Button onClick={() => navigate('/leases')}>{t('messages.back_to_leases')}</Button>}
          />
        </Card>
    )
  }

  // EP-156 (T156-010, §3.3/A6) — the confirm may only promise the refund
  // expense when has_renewal reads exactly `false`; missing (GET
  // /api/leases/:id doesn't serialize it yet) or `true` both fall back to
  // the plain end_confirm message, per the design note.
  // GET /api/leases/:id renders decimals as strings ("1000.0"), so coerce
  // before adding or "1000.0" + "200.0" concatenates.
  const endRefundAmount = Number(lease.move_in_fee_amount ?? 0) + Number(lease.keycard_deposit_amount ?? 0)
  const endWillCreateExpense = lease.has_renewal === false && endRefundAmount > 0
  const endConfirmMessage = endWillCreateExpense
    ? t('messages.end_confirm_with_expense', { amount: formatBaht(endRefundAmount) })
    : t('messages.end_confirm')

  const allSigned = signatures && signatures.length > 0 && signatures.every((s) => s.signed)
  // Shared by TenantVehiclesCard, LprEventsCard and SettlementCard (EP-130 T130-012).
  const propertyId = (lease.unit as { room_type?: { property?: { id?: number } } } | undefined)
    ?.room_type?.property?.id

  return (
    <>
      <div className="space-y-6">
        <ExploreCoachBanner pageKey="lease" />
        <Breadcrumbs items={[
          {
            label: isCondominium ? tCon('list.title.ownership') : t('title'),
            href: '/leases',
          },
          // The lease code is not sent in a support session (EP-160).
          { label: lease.lease_code ?? `#${lease.id}` },
        ]} />

        <PageHeader
          title={
            isCondominium
              ? tCon('detail.header.ownership', { code: lease.lease_code ?? `#${lease.id}` })
              : (lease.lease_code ?? `#${lease.id}`)
          }
          description={
            isCondominium
              ? undefined
              : t('messages.detail_description', { date: formatDate(lease.started_at) })
          }
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — primary is the lifecycle-ending action (only while
          // active); everything else (sign links, activate, PDF, record
          // payment, edit, delete) lives in the overflow. When there's no
          // active lease to end, the first visible overflow entry promotes to
          // solid so mobile stays [ solid ][ ⋯ ].
          primaryAction={
            lease.status === 'active' && canMutateLease
              ? {
                  key: 'end-lease',
                  label: t('actions.end_lease'),
                  icon: XCircle,
                  onClick: () => setConfirmEnd(true),
                  loading: endLease.isPending,
                  testId: 'lease-end-lease',
                }
              : undefined
          }
          overflowActions={[
            // EP-130 (T130-011) — Bill deposit. Kept FIRST so it is the
            // action that promotes to the mobile solid slot on
            // pending_start (no primaryAction exists in that state; the
            // layout promotes the first VISIBLE overflow entry).
            {
              key: 'bill-deposit',
              label: t('actions.bill_deposit'),
              icon: Wallet,
              onClick: handleBillDeposit,
              loading: createDepositInvoice.isPending,
              hidden:
                isCondominium ||
                !canMutateLease ||
                !lease.deposit_summary ||
                lease.deposit_summary.amount <= 0 ||
                // EP-130 allowed this only after signing. EP-136 adds exactly one
                // case: a BOOKED draft whose booking fee is already paid, whose
                // remainder must be billable while the lease is still unsigned —
                // otherwise the booking flow dead-ends with nothing to click.
                // An ordinary unbooked draft stays hidden, as EP-130 intended.
                //
                // The paid check is not cosmetic: the server refuses an unpaid
                // booking with booking_invoice_unpaid (owner ruling R2), and an
                // action visible only to 422 on click is the exact pattern this
                // epic has already had to unpick twice.
                !(
                  ['pending_start', 'active'].includes(lease.status) ||
                  (lease.status === 'draft' &&
                    lease.deposit_summary.booking_invoice != null &&
                    lease.deposit_summary.booking_invoice.paid)
                ) ||
                lease.deposit_summary.deposit_invoice != null ||
                // T149-079 (MON-7 C) follow-up: a deposit held before FlowRent
                // can, on its own or together with a paid booking fee, already
                // reach the full deposit with no invoice on either side.
                // `collected` is the server-derived boolean that already sums
                // every source (deposit line, booking fee, held amount), so
                // checking it here covers "held covers the deposit" without
                // the FE re-deriving that sum. While held is less than the
                // deposit, `collected` stays false and the action stays
                // visible, exactly as required: the server bills only the
                // remainder.
                lease.deposit_summary.collected,
              testId: 'lease-bill-deposit',
            },
            // EP-59 V6: e-sign UI is hidden entirely for ownership records.
            // EP-162 item 11 follow-up 1: owner/admin (LeasePolicy#renew?),
            // shown when the server says the lease can be renewed, a lease up
            // to 90 days past its end included.
            {
              key: 'renew-lease',
              label: t('actions.renew_lease'),
              icon: Repeat,
              onClick: () => setConfirmRenew(true),
              loading: renewLease.isPending,
              hidden: !(lease.renewable === true && isAdminOrOwner),
              testId: 'lease-renew',
            },
            {
              key: 'generate-sign-links',
              label: t('actions.generate_sign_links'),
              icon: FileSignature,
              onClick: () => setShowSignModal(true),
              hidden: pdfHidden || !(!isCondominium && (lease.status === 'pending_signatures' ||
                ((lease.status === 'draft' || lease.status === 'active') && lease.pdf_key && isAdminOrOwner))),
            },
            {
              key: 'activate-lease',
              label: t('actions.activate_lease'),
              icon: CheckCircle,
              onClick: handleActivate,
              hidden: !((lease.status === 'draft' || lease.status === 'pending_signatures') && isAdminOrOwner),
            },
            {
              key: 'download-pdf',
              label: t('actions.download_pdf'),
              icon: Download,
              loading: isDownloadingPdf,
              // Hidden once the probe proves the object is gone — the rebuild
              // action below takes over for that case.
              hidden: !lease.pdf_key || storedPdfMissing,
              testId: 'lease-download-pdf',
              // A real anchor, not a blob download: <a download> and
              // createObjectURL are no-ops in the Capacitor WKWebView, so the
              // old handler silently did nothing on mobile. /api/images/:key
              // 302s to a fresh presigned URL on every hit, so the href never
              // goes stale the way an inlined presigned URL would, and a
              // top-level navigation follows the redirect without the CORS
              // problem that affects XHR (see lib/imageUrl).
              href: `${import.meta.env.VITE_API_URL || ''}/api/images/${lease.pdf_key}`,
              external: true,
            },
            {
              // Same button, but for a pdf_key whose object no longer exists:
              // blobs written before the S3 cutover lived only in the Tigris
              // bucket (deleted 2026-08-18), so 129 leases point at nothing.
              // Rebuilding from the template beats dead-ending the user.
              //
              // What comes back is NOT the original: signatures are embedded in
              // the PDF and never stored separately, so a regenerated document
              // is unsigned. The toast says so rather than quietly substituting
              // it. lease_signatures still records who signed and when.
              key: 'rebuild-missing-pdf',
              label: t('actions.download_pdf'),
              icon: Download,
              loading: isDownloadingPdf,
              hidden: !lease.pdf_key || !storedPdfMissing || !isAdminOrOwner,
              testId: 'lease-rebuild-pdf',
              onClick: async () => {
                setIsDownloadingPdf(true)
                const filename = `lease-${lease.id}.pdf`
                try {
                  const template = (lease.document_template?.template || templatesData?.data?.[0]?.template) as import('@pdfme/common').Template | undefined
                  if (!template) throw new Error(t('errors.lease_template_missing'))
                  const { generateAndUploadLeasePdf, downloadStoredPdf } = await import('@/lib/pdfGenerator')
                  const pdfKey = await generateAndUploadLeasePdf(lease, template)
                  await updateLease.mutateAsync({ id: lease.id, pdf_key: pdfKey })
                  setStoredPdfMissing(false)
                  await downloadStoredPdf(pdfKey, filename)
                  addToast({ type: 'info', message: t('messages.pdf_regenerated') })
                } catch (error) {
                  console.error('Failed to rebuild lease PDF:', error)
                  addToast({ type: 'error', message: translateError(error) })
                } finally {
                  setIsDownloadingPdf(false)
                }
              },
            },
            {
              key: 'generate-pdf',
              label: t('actions.generate_pdf'),
              icon: Download,
              loading: isGeneratingPdf,
              disabled: !lease.document_template?.template && !templatesData?.data?.[0]?.template,
              hidden: !!lease.pdf_key || !isAdminOrOwner || pdfHidden,
              onClick: async () => {
                setIsGeneratingPdf(true)
                try {
                  const template = (lease.document_template?.template || templatesData?.data?.[0]?.template) as import('@pdfme/common').Template | undefined
                  if (!template) throw new Error(t('errors.lease_template_missing'))
                  const { generateAndUploadLeasePdf } = await import('@/lib/pdfGenerator')
                  const pdfKey = await generateAndUploadLeasePdf(lease, template)
                  await updateLease.mutateAsync({ id: lease.id, pdf_key: pdfKey })
                } catch (error) {
                  console.error('Failed to generate PDF:', error)
                  addToast({ type: 'error', message: translateError(error) })
                } finally {
                  setIsGeneratingPdf(false)
                }
              },
            },
            // P005: Record Payment — hidden for condominium (rent=0).
            {
              key: 'record-payment',
              label: tc('actions.record_payment'),
              icon: CreditCard,
              to: `/payments/record?lease_id=${leaseId}`,
              hidden: isCondominium,
              testId: 'lease-record-payment',
            },
            // Edit and Delete — admin/owner only.
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/leases/${leaseId}/edit`,
              hidden: !isAdminOrOwner,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setConfirmDelete(true),
              destructive: true,
              // T158-009: the server already refuses a non-draft lease (422
              // lease_not_draft) — hide the action rather than offer a
              // refusal the payload already shows. A draft lease with
              // invoices is still offered (lease_has_invoices isn't knowable
              // from this payload) and surfaces via the toast on confirm.
              // EP-158 T158-009: rental leases only while draft; an ownership
              // contract (no draft state) stays deletable for EP-134's undo.
              hidden: !isAdminOrOwner || (lease.status !== 'draft' && lease.contract_type !== 'ownership'),
            },
          ]}
        />

        {/* Lease Info */}
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4">{t('lease_detail')}</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.start_date')}
                </dt>
                <dd className="font-medium">{formatDate(lease.started_at)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.end_date')}
                </dt>
                <dd className="font-medium">
                  {lease.ended_at ? formatDate(lease.ended_at) : t('messages.ongoing')}
                </dd>
              </div>
              {/* EP-59 V6 — monthly rent + deposit are omitted entirely
                  for ownership records (always 0, would be misleading). */}
              {!isCondominium && (
                <>
                  <div className="min-w-0">
                    <dt className="text-sm text-base-content/60 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" /> {t('labels.monthly_rent')}
                      {/* EP-57 — show "Pinned at activation" badge when the lease has its own
                          snapshot. For legacy leases without a snapshot, fall back to unit.price
                          silently (no badge — the value is still correct, just live-looked-up). */}
                      {lease.rent_amount != null && (
                        <Badge variant="ghost" className="text-xs">
                          {t('labels.pinned_at_activation')}
                        </Badge>
                      )}
                    </dt>
                    <dd className="font-medium text-lg">
                      {formatBaht(lease.rent_amount ?? lease.unit?.price ?? 0)}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-sm text-base-content/60 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" /> {t('labels.deposit')}
                    </dt>
                    <dd className="font-medium text-lg">{formatBaht(lease.deposit)}</dd>
                  </div>
                  {/* EP-156 (T156-010, §3.3/S7) — shown only when set (null
                      by default, no property default). */}
                  {lease.move_in_fee_amount != null && (
                    <div className="min-w-0">
                      <dt className="text-sm text-base-content/60 flex items-center gap-1">
                        <DollarSign className="w-4 h-4" /> {t('labels.move_in_fee')}
                      </dt>
                      <dd className="font-medium text-lg">{formatBaht(lease.move_in_fee_amount)}</dd>
                    </div>
                  )}
                  {lease.keycard_deposit_amount != null && (
                    <div className="min-w-0">
                      <dt className="text-sm text-base-content/60 flex items-center gap-1">
                        <DollarSign className="w-4 h-4" /> {t('labels.keycard_deposit')}
                      </dt>
                      <dd className="font-medium text-lg">{formatBaht(lease.keycard_deposit_amount)}</dd>
                    </div>
                  )}
                </>
              )}
              <div className="col-span-full min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.status')}</dt>
                <dd className="mt-1">{getStatusBadge(lease.status)}</dd>
              </div>
            </dl>
          </Card>

          <div className="space-y-6">
            {/* Customer Card */}
            <Card>
              <h2 className="text-lg font-semibold mb-4">{t('labels.tenant')}</h2>
              {lease.customer ? (
                <Link
                  to={`/customers/${lease.customer_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-base-200 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">{lease.customer.name}</div>
                    <div className="text-sm text-base-content/60">{lease.customer.phone}</div>
                  </div>
                </Link>
              ) : (
                <p className="text-base-content/60">#{lease.customer_id}</p>
              )}
            </Card>

            {/* Unit Card */}
            <Card>
              <h2 className="text-lg font-semibold mb-4">{t('labels.unit')}</h2>
              {lease.unit ? (
                <Link
                  to={`/units/${lease.unit_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-base-200 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Home className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">{lease.unit.name}</div>
                    <div className="text-sm text-base-content/60">
                      {formatBaht(lease.unit.price)}{t('messages.per_month')}
                    </div>
                  </div>
                </Link>
              ) : (
                <p className="text-base-content/60">#{lease.unit_id}</p>
              )}
            </Card>
          </div>
        </div>

        {/* EP-130 (T130-011): Deposit panel — non-condo leases with a deposit
            to track. */}
        {!isCondominium && lease.deposit_summary && lease.deposit_summary.amount > 0 && (
          <DepositPanelCard
            summary={lease.deposit_summary}
            onRecordHeld={openDepositHeldModal}
            canRecordHeld={canMutateLease}
          />
        )}

        {/* EP-130 (T130-012): Settlement card — sibling of the deposit panel,
            only once the lease has ended_at (any date, past or future — Q-3;
            no <= today guard) and there is a positive deposit to settle. */}
        {!isCondominium && lease.ended_at && lease.deposit_summary && lease.deposit_summary.amount > 0 && (
          <SettlementCard
            leaseId={leaseId}
            summary={lease.deposit_summary}
            propertyId={propertyId}
            cmmsAvailable={cmmsAvailable}
          />
        )}

        {/* EP85-P3-05: Tenant Vehicles card — active tenant vehicles for this lease */}
        <TenantVehiclesCard
          leaseId={leaseId}
          customerId={lease.customer_id}
          propertyId={propertyId}
        />

        {/* EP85-P3-06: LPR Events card — last 10 events for tenant vehicles.
            Hidden when no tenant vehicles are registered, or when no LPR events exist
            (property has no cameras). */}
        {lprEnabled && (
          <LprEventsCard
            leaseId={leaseId}
            propertyId={propertyId}
          />
        )}

        {/* Signature Status Panel */}
        {signatures && signatures.length > 0 && (
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <FileSignature className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">{t('signature_panel.title')}</h2>
              {allSigned && (
                <Badge variant="success" className="ml-2">{t('signature_panel.all_signed')}</Badge>
              )}
            </div>
            <div className="space-y-3">
              {signatures.map((sig) => (
                <div
                  key={sig.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-base-200"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${sig.signed ? 'bg-success' : sig.expired ? 'bg-error' : 'bg-warning'}`} />
                    <div>
                      <div className="font-medium">
                        {signerRoleLabel(sig.signer_role)} — {sig.signer_name}
                      </div>
                      <div className="text-sm text-base-content/60">
                        {sig.signed
                          ? `${t('signature_panel.signed')}${sig.signed_at ? ` · ${formatDate(sig.signed_at)}` : ''}`
                          : sig.expired
                            ? t('signature_panel.expired')
                            : sig.expires_at
                              ? t('signature_panel.expires_at', { date: formatDateTime(sig.expires_at) })
                              : t('signature_panel.unsigned')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!sig.signed && isAdminOrOwner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<RefreshCw className="w-3 h-3" />}
                        onClick={() => openRegenModal(sig)}
                      >
                        {t('signature_panel.regenerate')}
                      </Button>
                    )}
                    {!sig.signed && !sig.expired && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<LinkIcon className="w-3 h-3" />}
                        onClick={() => handleCopyLink(sig)}
                      >
                        {copiedId === sig.id ? t('signature_panel.link_copied') : t('signature_panel.copy_link')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* EPV-11: Vehicle reassignment section — only for ended leases */}
        {lease.status === 'ended' && (
          <VehicleReassignSection leaseId={leaseId} />
        )}

        {/* Attachments Section */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Paperclip className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">{t('attachments.title')}</h2>
          </div>
          <div className="space-y-6">
            {hiddenInSession(lease, 'pdf_key') && (
              <div data-testid="lease-pdf-hidden">
                <h3 className="text-sm font-medium text-base-content/70 mb-3">{tc('support_session.lease_pdf')}</h3>
                <SupportSessionHidden testId="lease-pdf-hidden-chip" />
              </div>
            )}
            {/* Images */}
            <div>
              <h3 className="text-sm font-medium text-base-content/70 mb-3">{t('attachments.images_section')}</h3>
              {imagesHidden ? (
                <SupportSessionHidden testId="lease-images-hidden-chip" />
              ) : (
                <FileUpload
                  value={imageUrls.map((key) =>
                    key.startsWith('/api/') || key.startsWith('http') ? key : `${API_BASE}/api/images/${key}`
                  )}
                  onChange={async (displayUrls) => {
                    // Convert display URLs back to keys for storage
                    const keys = displayUrls.map((url) => {
                      const match = url.match(/\/api\/images\/(.+)$/)
                      return match ? match[1] : url
                    })
                    setLocalImageUrls(keys)
                    await updateLease.mutateAsync({ id: leaseId, image_urls: keys })
                  }}
                  maxFiles={10}
                  accept="image/*"
                  onUpload={async (file) => {
                    const url = await uploadLeaseFile(file)
                    return url
                  }}
                />
              )}
            </div>

            {/* Files / Attachments */}
            {attachmentsHidden ? (
              <div>
                <h3 className="text-sm font-medium text-base-content/70 mb-3">{t('attachments.files_section')}</h3>
                <SupportSessionHidden testId="lease-attachments-hidden-chip" />
              </div>
            ) : (
              <AttachmentList
                attachmentUrls={attachmentUrls}
                label={t('attachments.files_section')}
                uploadLabel={t('attachments.upload_file')}
                removeLabel={tc('actions.remove')}
                apiBase={API_BASE}
                onRemove={async (updated) => {
                  setLocalAttachmentUrls(updated)
                  await updateLease.mutateAsync({ id: leaseId, attachment_urls: updated })
                }}
                onUpload={async (file) => {
                  const key = await uploadLeaseFileRaw(file)
                  const updated = [...attachmentUrls, key]
                  setLocalAttachmentUrls(updated)
                  await updateLease.mutateAsync({ id: leaseId, attachment_urls: updated })
                }}
              />
            )}
          </div>
        </Card>
      </div>

      {/* Generate Sign Links Modal */}
      <Modal
        isOpen={showSignModal}
        onClose={() => setShowSignModal(false)}
        title={t('sign_modal.title')}
        size="md"
        footer={
          <div className="flex gap-2 w-full justify-end">
            <Button variant="ghost" onClick={() => setShowSignModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleGenerateSignLinks}
              loading={generateSignLinks.isPending}
            >
              {t('sign_modal.generate')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Tenant name — auto-filled, read-only */}
          <Input
            label={t('sign_modal.tenant_name')}
            value={lease?.customer?.name ?? ''}
            readOnly
            inputClassName="bg-base-200"
          />

          {/* Owner name */}
          <Input
            label={t('sign_modal.owner_name')}
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder={t('sign_modal.owner_name')}
          />

          {/* Witness 1 */}
          <Input
            label={t('sign_modal.witness_1')}
            value={witness1}
            onChange={(e) => setWitness1(e.target.value)}
            placeholder={t('sign_modal.witness_1')}
          />

          {/* Witness 2 */}
          <Input
            label={t('sign_modal.witness_2')}
            value={witness2}
            onChange={(e) => setWitness2(e.target.value)}
            placeholder={t('sign_modal.witness_2')}
          />
        </div>
      </Modal>

      {/* EP-149 T149-079 (MON-7 C): Record deposit held before FlowRent */}
      <Modal
        isOpen={showDepositHeldModal}
        onClose={() => setShowDepositHeldModal(false)}
        title={t('deposit_panel.record_held_before_flowrent')}
        size="sm"
        footer={
          <div className="flex gap-2 w-full justify-end">
            <Button variant="ghost" onClick={() => setShowDepositHeldModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleRecordDepositHeld}
              loading={recordDepositHeld.isPending}
              disabled={!depositHeldAmount || Number(depositHeldAmount) <= 0 || !depositHeldDate}
              data-testid="deposit-held-save"
            >
              {tc('actions.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-base-content/60">
            {t('deposit_panel.held_before_flowrent_hint')}
          </p>
          <Input
            label={t('deposit_panel.held_before_flowrent_amount_label')}
            type="number"
            step="0.01"
            value={depositHeldAmount}
            onChange={(e) => setDepositHeldAmount(e.target.value)}
            data-testid="deposit-held-amount"
          />
          <DateInput
            id="deposit-held-date"
            label={t('deposit_panel.held_before_flowrent_date_label')}
            value={depositHeldDate}
            onChange={(e) => setDepositHeldDate(e.target.value)}
            dateFormat={dateFormat}
            timezone={timezone}
            calendar={calendar}
            locale={i18n.language}
          />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={t('messages.delete_title')}
        message={t('messages.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteLease.isPending}
      />

      <ConfirmDialog
        isOpen={confirmEnd}
        onClose={() => setConfirmEnd(false)}
        onConfirm={handleEnd}
        title={t('actions.end_lease')}
        message={endConfirmMessage}
        confirmLabel={t('actions.end_lease')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={endLease.isPending}
      />

      <ConfirmDialog
        isOpen={confirmRenew}
        onClose={() => setConfirmRenew(false)}
        onConfirm={handleRenew}
        title={t('actions.renew_lease')}
        message={t('messages.renew_confirm')}
        confirmLabel={t('actions.renew_lease')}
        cancelLabel={tc('actions.cancel')}
        loading={renewLease.isPending}
      />

      {/* Regenerate Sign Link Modal */}
      <Modal
        isOpen={!!regenSig}
        onClose={() => setRegenSig(null)}
        title={t('signature_panel.regenerate_title')}
        size="sm"
        footer={
          <div className="flex gap-2 w-full justify-end">
            <Button variant="ghost" onClick={() => setRegenSig(null)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleRegenerate}
              loading={regenerateSignLink.isPending}
            >
              {t('signature_panel.regenerate')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('signature_panel.signer_name')}
            value={regenName}
            onChange={(e) => setRegenName(e.target.value)}
          />
        </div>
      </Modal>
    </>
  )
}
